import { useState } from "react";
import moment from "moment-timezone";
import { SendIcon, UploadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { CommonDestinationTimezone } from "@/lib/utils";
import type { SelectOptionItem } from "@/lib/models/ui";
import type { CfrInstanceVisibility } from "@/lib/api/cfr";
import { ContractListSection } from "../ContractList";
import { CreateInstanceDialog } from "../components/CreateInstanceDialog";
import { ManualCfrUploadDialog } from "../components/ManualCfrUploadDialog";
import {
  type CreateInstanceDialogOptions,
  generateDefaultEmptyInstanceInfo,
} from "../List";

const MIN_CROP_YEAR = 2024;
const currentYear = moment.tz(CommonDestinationTimezone).year();

const cropYearOptions: SelectOptionItem<string>[] = [];
for (let y = currentYear; y >= MIN_CROP_YEAR; y--) {
  cropYearOptions.push({ value: String(y), label: String(y) });
}

const visibilityOptions: SelectOptionItem<CfrInstanceVisibility>[] = [
  { value: "visible", label: "Active" },
  { value: "hidden", label: "Archived" },
];

export function CfrInstancesPage() {
  const [createInstanceInfo, setCreateInstanceInfo] =
    useState<CreateInstanceDialogOptions>(generateDefaultEmptyInstanceInfo());
  const [cropYearValue, setCropYearValue] = useState<string>(String(currentYear));
  const [visibilityValue, setVisibilityValue] = useState<CfrInstanceVisibility>("visible");
  const [isManualUploadOpen, setIsManualUploadOpen] = useState(false);

  const handleInstanceClick = (row: { customer_id?: string; id: string }) => {
    setCreateInstanceInfo({
      ...generateDefaultEmptyInstanceInfo(),
      suggestionId: "",
      selectedCustomerId: row.customer_id || "",
      selectedTemplateId: row.id || "",
      isDialogOpen: true,
    });
  };

  const handleBlankSend = () => {
    setCreateInstanceInfo({
      ...generateDefaultEmptyInstanceInfo(),
      isDialogOpen: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
        </div>
        <div className="flex gap-2">
          <Combobox
            id="cfr-instances-visibility"
            placeholder="Visibility"
            options={visibilityOptions}
            value={visibilityValue}
            onValueChange={(value: string) => setVisibilityValue(value === "hidden" ? "hidden" : "visible")}
            className="w-[160px]"
          />
          <Combobox
            id="cfr-instances-crop-year"
            placeholder="Crop Year"
            options={cropYearOptions}
            value={cropYearValue}
            onValueChange={setCropYearValue}
            className="w-[160px]"
          />
          <Button variant="outline" onClick={() => setIsManualUploadOpen(true)}>
            <UploadIcon className="mr-2 h-4 w-4" />
            Upload Manual CFR
          </Button>
          <Button variant="outline" onClick={handleBlankSend}>
            <SendIcon className="mr-2 h-4 w-4" />
            Send Template
          </Button>
        </div>
      </div>

      <ContractListSection
        onInstanceClick={handleInstanceClick}
        cropYear={cropYearValue === "" ? undefined : Number(cropYearValue)}
        visibility={visibilityValue}
      />

      <ManualCfrUploadDialog
        open={isManualUploadOpen}
        onOpenChange={setIsManualUploadOpen}
        onSuccess={() => setIsManualUploadOpen(false)}
      />

      {createInstanceInfo.isDialogOpen && (
        <CreateInstanceDialog
          open={createInstanceInfo.isDialogOpen}
          onOpenChange={(status: boolean) => {
            setCreateInstanceInfo((prev) => ({
              ...prev,
              isDialogOpen: status,
            }));
          }}
          onSuccess={() => {}}
          initialData={{
            customerId: createInstanceInfo.selectedCustomerId,
            templateId: createInstanceInfo.selectedTemplateId,
            suggestionId: createInstanceInfo.suggestionId,
            contractNumber: createInstanceInfo.contractNumber,
            contractType: createInstanceInfo.contractType,
            contractQuantity: createInstanceInfo.contractQuantity,
            documentTitle: createInstanceInfo.documentTitle,
          }}
        />
      )}

    </div>
  );
}

